import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { KpiGrid, KpiCard, StatusChip } from "@/components/kcc/Kpi";
import { Panel, ModuleHeader } from "@/components/kcc/Panel";
import { DataTable, statusToChip } from "@/components/kcc/DataTable";
import { KccBarChart, KccLineChart } from "@/components/kcc/Charts";
import { PredictivePanel, ReliabilityPanel, ContractorsPanel, KnowledgePanel, ReportsPanel } from "@/pages/RefineryPanels";
import { X, Loader2, Wrench, ShieldCheck, Zap, Droplets, Beaker, AlertTriangle, Bot } from "lucide-react";

const STATUS_STYLES = {
    running: { s: "normal", label: "Running" },
    idle: { s: "info", label: "Idle" },
    planned_maintenance: { s: "info", label: "Planned Maintenance" },
    shutdown: { s: "warning", label: "Shutdown" },
    breakdown: { s: "critical", label: "Breakdown" },
    alarm: { s: "warning", label: "Alarm" },
    critical: { s: "critical", label: "Critical" },
    inspection: { s: "info", label: "Under Inspection" },
};

function StatusPill({ status }) {
    const cfg = STATUS_STYLES[status] || { s: "info", label: status };
    return <StatusChip status={cfg.s}>{cfg.label}</StatusChip>;
}

export default function RefineryMaintenance() {
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("overview");
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [ai, setAi] = useState(null);
    const [aiInput, setAiInput] = useState("");
    const [aiSending, setAiSending] = useState(false);
    const [assetTypeFilter, setAssetTypeFilter] = useState("");

    useEffect(() => { api.get("/refinery-maintenance/overview").then(({ data }) => setData(data)); }, []);

    async function openAsset(a) {
        setSelected(a);
        setDetail(null);
        setLoading(true);
        try {
            const { data } = await api.get(`/refinery-maintenance/asset/${a.id}`);
            setDetail(data);
        } finally { setLoading(false); }
    }

    async function askAI(msg) {
        const text = (msg ?? aiInput).trim();
        if (!text || aiSending) return;
        setAiInput("");
        setAi((prev) => ({ ...(prev || { messages: [] }), messages: [...((prev && prev.messages) || []), { role: "user", content: text }] }));
        setAiSending(true);
        try {
            const { data } = await api.post("/ai/agent", { agent_id: "refinery_maintenance", message: text, session_id: ai?.session_id || null });
            setAi((prev) => ({
                session_id: data.session_id,
                messages: [...((prev && prev.messages) || []), { role: "assistant", content: data.reply, sources: data.sources || [] }],
            }));
        } catch (e) {
            setAi((prev) => ({ ...prev, messages: [...((prev && prev.messages) || []), { role: "assistant", content: `⚠︎ ${e.response?.data?.detail || e.message}` }] }));
        } finally { setAiSending(false); }
    }

    const assets = data?.assets || [];
    const filteredAssets = useMemo(
        () => (assetTypeFilter ? assets.filter((a) => a.type === assetTypeFilter) : assets),
        [assets, assetTypeFilter]
    );

    const statusChart = (data?.status_distribution || []).map((s) => ({ name: STATUS_STYLES[s.status]?.label || s.status, value: s.count }));

    const worstHealth = [...assets].sort((a, b) => a.health_index - b.health_index).slice(0, 8).map((a) => ({ name: a.tag, value: a.health_index }));

    const budget = data?.budget || [];
    const impact = data?.process_impact || {};

    const openWos = (data?.work_orders || []).filter((w) => w.status !== "Completed" && w.status !== "Verified");
    const upcomingPMs = assets.filter((a) => a.next_pm && new Date(a.next_pm) > new Date()).sort((a, b) => new Date(a.next_pm) - new Date(b.next_pm)).slice(0, 10);
    const overduePMs = assets.filter((a) => a.next_pm && new Date(a.next_pm) < new Date()).sort((a, b) => new Date(a.next_pm) - new Date(b.next_pm)).slice(0, 10);
    const lowRUL = [...assets].sort((a, b) => a.rul_days - b.rul_days).slice(0, 8);

    return (
        <div className="space-y-6">
            <ModuleHeader
                kicker="Refinery Maintenance Manager360™"
                title="Refinery Maintenance Command Centre"
                description="Every KPI, every asset, every work order, every reliability decision — one governed workspace for the Refinery Maintenance Manager. Integrates predictive maintenance, safety controls, spare-parts governance and AI recommendations."
                actions={
                    <div className="flex flex-wrap gap-2">
                        {["overview", "live_status", "work_orders", "reliability", "predictive", "shutdowns", "spares", "workforce", "budget", "contractors", "knowledge", "reports", "ai"].map((t) => (
                            <button
                                key={t}
                                onClick={() => setTab(t)}
                                data-testid={`rmm-tab-${t}`}
                                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${tab === t ? "border-kcc-copper bg-kcc-copper/15 text-white" : "border-white/10 text-kcc-silver hover:text-white"}`}
                            >
                                {t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                            </button>
                        ))}
                    </div>
                }
            />

            <KpiGrid kpis={(data?.kpis || []).slice(0, 8)} accent="copper" cols={4} />
            <KpiGrid kpis={(data?.kpis || []).slice(8, 16)} accent="cobalt" cols={4} />

            {tab === "overview" && (
                <>
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                        <Panel title="Asset Status Distribution" subtitle="Refinery-wide" accent="copper">
                            <KccBarChart data={statusChart} dataKey="value" label="Assets" color="#b87333" />
                        </Panel>
                        <Panel title="Lowest Health Assets" subtitle="Bad-actor watch-list" accent="red">
                            <KccBarChart data={worstHealth} dataKey="value" label="Health" color="#ef4444" />
                        </Panel>
                        <Panel title="Refinery Process Impact" subtitle="Consequences of current maintenance state" accent="cobalt">
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div className="p-3 rounded border border-white/5"><div className="kcc-label">Cu production loss</div><div className="font-display text-xl text-white mt-1">{impact.copper_production_loss_t?.toLocaleString()} t</div></div>
                                <div className="p-3 rounded border border-white/5"><div className="kcc-label">Co production loss</div><div className="font-display text-xl text-white mt-1">{impact.cobalt_production_loss_t} t</div></div>
                                <div className="p-3 rounded border border-white/5"><div className="kcc-label">Recovery impact</div><div className="font-display text-xl text-white mt-1">{impact.recovery_impact_pct}%</div></div>
                                <div className="p-3 rounded border border-white/5"><div className="kcc-label">Throughput impact</div><div className="font-display text-xl text-white mt-1">{impact.throughput_impact_pct}%</div></div>
                                <div className="p-3 rounded border border-white/5"><div className="kcc-label">Downtime (mo)</div><div className="font-display text-xl text-white mt-1">{impact.downtime_h_month} h</div></div>
                                <div className="p-3 rounded border border-white/5"><div className="kcc-label">Quality holds</div><div className="font-display text-xl text-white mt-1">{impact.quality_holds}</div></div>
                                <div className="p-3 rounded border border-white/5 flex items-center gap-2"><Zap className="w-4 h-4 text-kcc-amber" /><div><div className="kcc-label">Energy impact</div><div className="font-display text-sm text-white mt-0.5">{impact.energy_impact_mwh?.toLocaleString()} MWh</div></div></div>
                                <div className="p-3 rounded border border-white/5 flex items-center gap-2"><Droplets className="w-4 h-4 text-kcc-water" /><div><div className="kcc-label">Water impact</div><div className="font-display text-sm text-white mt-0.5">{impact.water_impact_km3} km³</div></div></div>
                                <div className="p-3 rounded border border-white/5 flex items-center gap-2 col-span-2"><Beaker className="w-4 h-4 text-kcc-copper-light" /><div><div className="kcc-label">Acid impact</div><div className="font-display text-sm text-white mt-0.5">{impact.acid_impact_t} t</div></div></div>
                            </div>
                        </Panel>
                    </div>

                    <Panel title="Safety Integration" subtitle="Work cannot start without valid Permit + LOTO + Isolation" accent="green">
                        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 text-sm">
                            {[
                                { k: "Permits to Work", v: `${openWos.filter((w) => w.permit_ready).length}/${openWos.length}`, s: "normal" },
                                { k: "LOTO Verified", v: `${openWos.filter((w) => w.isolation_verified).length}/${openWos.length}`, s: "normal" },
                                { k: "Confined-Space Entry", v: "3 active", s: "warning" },
                                { k: "Hot Work Permits", v: "2 active", s: "warning" },
                                { k: "Working-at-Height", v: "4 active", s: "warning" },
                                { k: "Risk Assessments", v: "All current", s: "normal" },
                            ].map((r) => (
                                <div key={r.k} className="p-3 rounded border border-white/5 flex items-center justify-between">
                                    <div>
                                        <div className="kcc-label">{r.k}</div>
                                        <div className="font-display text-white mt-1">{r.v}</div>
                                    </div>
                                    <ShieldCheck className={`w-4 h-4 ${r.s === "warning" ? "text-kcc-amber" : "text-kcc-green"}`} />
                                </div>
                            ))}
                        </div>
                    </Panel>
                </>
            )}

            {tab === "live_status" && (
                <Panel title="Live Plant Status by Equipment Family" subtitle="Real-time refinery equipment health" accent="copper">
                    <DataTable
                        columns={[
                            { key: "type", label: "Family" },
                            { key: "area", label: "Area" },
                            { key: "count", label: "Assets" },
                            { key: "running", label: "Running" },
                            { key: "critical", label: "Critical" },
                            { key: "alarm", label: "Alarm" },
                            { key: "breakdown", label: "Breakdown" },
                            { key: "avg_health", label: "Avg Health", render: (r) => <span className={r.avg_health < 70 ? "text-kcc-red" : r.avg_health < 82 ? "text-kcc-amber" : "text-kcc-green"}>{r.avg_health}</span> },
                        ]}
                        rows={data?.live_status || []}
                        testid="live-status-table"
                    />
                </Panel>
            )}

            {tab === "work_orders" && (
                <Panel title="Refinery Work Orders" subtitle={`${openWos.length} open of ${(data?.work_orders || []).length} total`} accent="amber">
                    <DataTable
                        columns={[
                            { key: "wo_no", label: "WO" },
                            { key: "asset_name", label: "Asset" },
                            { key: "area", label: "Area" },
                            { key: "type", label: "Type" },
                            { key: "priority", label: "Pri", render: (r) => <span className={r.priority === "P1" ? "text-kcc-red font-mono-kcc" : r.priority === "P2" ? "text-kcc-amber font-mono-kcc" : "font-mono-kcc kcc-text-silver"}>{r.priority}</span> },
                            { key: "description", label: "Description" },
                            { key: "technician", label: "Assigned" },
                            { key: "estimated_h", label: "Est h" },
                            { key: "total_cost_usd", label: "Cost", render: (r) => `$${r.total_cost_usd.toLocaleString()}` },
                            { key: "permit_ready", label: "Permit", render: (r) => <StatusChip status={r.permit_ready ? "normal" : "warning"}>{r.permit_ready ? "OK" : "Missing"}</StatusChip> },
                            { key: "isolation_verified", label: "LOTO", render: (r) => <StatusChip status={r.isolation_verified ? "normal" : "warning"}>{r.isolation_verified ? "OK" : "Missing"}</StatusChip> },
                            { key: "status", label: "Status", render: (r) => <StatusChip status={statusToChip(r.status)}>{r.status}</StatusChip> },
                        ]}
                        rows={(data?.work_orders || []).slice(0, 60)}
                        testid="refinery-wo"
                    />
                </Panel>
            )}

            {tab === "reliability" && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    <Panel title="Preventive Maintenance" subtitle="Compliance & schedule" accent="green">
                        <div className="grid grid-cols-3 gap-3 mb-4">
                            <KpiCard kpi={{ key: "pm", label: "PM Compliance", value: `${data?.pm?.compliance || 0}%`, status: (data?.pm?.compliance || 0) < 85 ? "warning" : "normal" }} accent="green" />
                            <KpiCard kpi={{ key: "od", label: "Overdue", value: String(data?.pm?.overdue || 0), status: (data?.pm?.overdue || 0) > 5 ? "critical" : "warning" }} accent="red" />
                            <KpiCard kpi={{ key: "up", label: "Upcoming", value: String(data?.pm?.upcoming || 0), status: "info" }} accent="cobalt" />
                        </div>
                        <div className="kcc-label mb-2">Overdue PMs</div>
                        <DataTable
                            columns={[
                                { key: "tag", label: "Tag" },
                                { key: "name", label: "Asset" },
                                { key: "area", label: "Area" },
                                { key: "next_pm", label: "Due", render: (r) => r.next_pm.slice(0, 10) },
                            ]}
                            rows={overduePMs}
                            testid="pm-overdue"
                        />
                    </Panel>
                    <Panel title="Reliability Engineering — Bad Actors, Failure Modes, Weibull" accent="red">
                        <ReliabilityPanel />
                    </Panel>
                </div>
            )}

            {tab === "predictive" && <PredictivePanel />}
            {tab === "contractors" && <ContractorsPanel />}
            {tab === "knowledge" && <KnowledgePanel />}
            {tab === "reports" && <ReportsPanel />}

            {tab === "shutdowns" && (
                <Panel title="Shutdown & Turnaround Calendar" accent="purple">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {[
                            { title: "Annual Refinery Turnaround", dates: "2026-10-14 → 2026-11-04", scope: "Cell house re-lining, SX mixer overhaul, acid plant catalyst change", status: "Planning", pct: 32 },
                            { title: "Q3 Concentrator Shutdown", dates: "2026-09-05 → 2026-09-12", scope: "SAG Mill 1 liner change, cyclone rebuild", status: "Scheduled", pct: 68 },
                            { title: "Emergency Shutdown — Substation A", dates: "2026-08-11 (48h window)", scope: "Transformer TR-33-01 replacement", status: "In Execution", pct: 78 },
                        ].map((s) => (
                            <div key={s.title} className="kcc-surface-elev p-4">
                                <div className="kcc-label text-kcc-copper-light">{s.status}</div>
                                <div className="font-display text-lg font-semibold mt-1 text-white">{s.title}</div>
                                <div className="text-[11px] kcc-text-silver mt-1">{s.dates}</div>
                                <p className="text-xs kcc-text-silver mt-2">{s.scope}</p>
                                <div className="mt-3">
                                    <div className="kcc-label">Readiness</div>
                                    <div className="w-full h-2 bg-white/10 rounded mt-1"><div className="h-2 rounded bg-gradient-to-r from-kcc-copper to-kcc-cobalt-light" style={{ width: `${s.pct}%` }} /></div>
                                    <div className="text-[11px] kcc-text-silver mt-1">{s.pct}% complete</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Panel>
            )}

            {tab === "spares" && (
                <Panel title="Critical Spare Parts" subtitle="Refinery consumption & rotables" accent="cobalt">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        {[
                            { k: "Critical spares stocked", v: "98%", s: "normal" },
                            { k: "Rotables in service", v: "42", s: "normal" },
                            { k: "Stock-outs (30d)", v: "3", s: "warning" },
                            { k: "Slow-moving lines", v: "12", s: "warning" },
                        ].map((k) => <KpiCard key={k.k} kpi={{ key: k.k, label: k.k, value: k.v, status: k.s }} accent="cobalt" />)}
                    </div>
                    <p className="text-xs kcc-text-silver">AI-forecast of spare demand for the next 90 days is available in <b>Predictive Analytics</b>. All movements are auditable and require Warehouse Manager approval above $5,000.</p>
                </Panel>
            )}

            {tab === "workforce" && (
                <Panel title="Maintenance Workforce" subtitle="Skills · shifts · productivity" accent="purple">
                    <DataTable
                        columns={[
                            { key: "employee_no", label: "ID" },
                            { key: "name", label: "Technician" },
                            { key: "trade", label: "Trade" },
                            { key: "shift", label: "Shift" },
                            { key: "certifications", label: "Certifications", render: (r) => r.certifications.join(", ") },
                            { key: "productivity_pct", label: "Productivity", render: (r) => `${r.productivity_pct}%` },
                            { key: "overtime_h_this_month", label: "OT (h)" },
                            { key: "training_due", label: "Training", render: (r) => r.training_due ? <StatusChip status="warning">Due</StatusChip> : <StatusChip status="normal">Current</StatusChip> },
                            { key: "available_today", label: "Today", render: (r) => r.available_today ? <StatusChip status="normal">Available</StatusChip> : <StatusChip status="warning">Off</StatusChip> },
                        ]}
                        rows={(data?.workforce || []).slice(0, 25)}
                        testid="workforce-table"
                    />
                </Panel>
            )}

            {tab === "budget" && (
                <>
                    <Panel title="Budget vs Actual by Area" subtitle="Refinery maintenance spend · US$ M" accent="cobalt">
                        <DataTable
                            columns={[
                                { key: "area", label: "Area" },
                                { key: "budget_musd", label: "Budget $M" },
                                { key: "actual_musd", label: "Actual $M" },
                                { key: "variance_pct", label: "Variance %", render: (r) => <span className={r.variance_pct > 8 ? "text-kcc-red" : r.variance_pct > 0 ? "text-kcc-amber" : "text-kcc-green"}>{r.variance_pct > 0 ? "+" : ""}{r.variance_pct}%</span> },
                                { key: "labour_musd", label: "Labour $M" },
                                { key: "materials_musd", label: "Materials $M" },
                                { key: "contractor_musd", label: "Contractor $M" },
                            ]}
                            rows={budget}
                        />
                    </Panel>
                </>
            )}

            {tab === "ai" && (
                <Panel title="AI Maintenance Advisor™" subtitle="Grounded in refinery equipment health · governed · explainable" accent="purple" className="min-h-[560px] flex flex-col">
                    <div className="flex-1 overflow-y-auto space-y-3 pr-1">
                        {(ai?.messages || []).length === 0 && (
                            <div className="text-sm kcc-text-silver bg-white/[0.03] border border-white/10 rounded-lg p-4">
                                Ask the Refinery Maintenance AI anything within its remit — e.g. <i>"which cell house pump is the biggest reliability risk?"</i> or <i>"optimise our annual turnaround scope"</i>.
                            </div>
                        )}
                        {(ai?.messages || []).map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-[86%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-kcc-cobalt/15 border border-kcc-cobalt/25 text-white" : "bg-white/[0.03] border border-white/8 text-kcc-silver"}`}>
                                    <div className="whitespace-pre-wrap">{m.content}</div>
                                </div>
                            </div>
                        ))}
                        {aiSending && <div className="text-sm kcc-text-silver flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Reasoning over refinery data…</div>}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {[
                            "Which cell house asset should we replace first?",
                            "Optimise our upcoming shutdown scope.",
                            "Rank my top 5 bad actors.",
                            "What spare parts will I need next 60 days?",
                        ].map((s) => (
                            <button key={s} onClick={() => askAI(s)} className="text-[11px] px-2.5 py-1.5 rounded-full border border-white/10 hover:border-kcc-purple/50 text-kcc-silver hover:text-white" data-testid="rmm-ai-suggestion">{s}</button>
                        ))}
                    </div>
                    <form onSubmit={(e) => { e.preventDefault(); askAI(); }} className="mt-3 flex items-end gap-2">
                        <textarea
                            value={aiInput}
                            onChange={(e) => setAiInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); askAI(); } }}
                            rows={1}
                            placeholder="Ask the Refinery Maintenance AI…"
                            className="flex-1 bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-kcc-purple/50 resize-none max-h-32"
                            data-testid="rmm-ai-input"
                        />
                        <button type="submit" disabled={aiSending || !aiInput.trim()} className="bg-kcc-copper hover:bg-kcc-copper-light disabled:opacity-40 text-white rounded-md px-3 py-2" data-testid="rmm-ai-send">
                            <Bot className="w-4 h-4" />
                        </button>
                    </form>
                </Panel>
            )}

            {/* Asset browser — always visible so the manager can drill into any equipment */}
            <Panel
                title="Refinery Asset Register"
                subtitle={`${filteredAssets.length} of ${assets.length} shown — click any asset for its digital passport`}
                accent="copper"
                actions={
                    <select
                        value={assetTypeFilter}
                        onChange={(e) => setAssetTypeFilter(e.target.value)}
                        className="bg-white/[0.03] border border-white/10 text-white text-xs rounded-md px-2.5 py-1.5 outline-none"
                        data-testid="asset-type-filter"
                    >
                        <option value="">All families</option>
                        {[...new Set(assets.map((a) => a.type))].map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                }
            >
                <DataTable
                    columns={[
                        { key: "tag", label: "Tag", render: (r) => <button onClick={() => openAsset(r)} className="font-mono-kcc text-kcc-copper-light hover:text-white" data-testid={`open-${r.id}`}>{r.tag}</button> },
                        { key: "name", label: "Asset" },
                        { key: "area", label: "Area" },
                        { key: "criticality", label: "Crit." },
                        { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
                        { key: "health_index", label: "Health", render: (r) => <span className={r.health_index < 70 ? "text-kcc-red" : r.health_index < 82 ? "text-kcc-amber" : "text-kcc-green"}>{r.health_index}</span> },
                        { key: "vibration_mms", label: "Vib mm/s" },
                        { key: "temperature_c", label: "Temp °C" },
                        { key: "availability_pct", label: "Avail %" },
                        { key: "oee_pct", label: "OEE %" },
                        { key: "rul_days", label: "RUL (d)", render: (r) => <span className={r.rul_days < 30 ? "text-kcc-red" : r.rul_days < 60 ? "text-kcc-amber" : "kcc-text-silver"}>{r.rul_days}</span> },
                    ]}
                    rows={filteredAssets.slice(0, 60)}
                    testid="refinery-assets"
                />
            </Panel>

            {selected && (
                <div className="fixed inset-0 z-50 flex justify-end animate-fade-in" data-testid="asset-drawer">
                    <div className="absolute inset-0 bg-black/60" onClick={() => setSelected(null)} />
                    <div className="relative kcc-glass w-full max-w-lg h-full overflow-y-auto p-6 border-l border-white/10 shadow-2xl">
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="kcc-label text-kcc-copper-light">Refinery Digital Passport</div>
                                <div className="font-display text-2xl font-semibold text-white mt-1">{selected.name}</div>
                                <div className="text-[11px] kcc-text-silver mt-1 font-mono-kcc">{selected.tag} · {selected.area}</div>
                                <div className="mt-2"><StatusPill status={selected.status} /></div>
                            </div>
                            <button onClick={() => setSelected(null)} className="text-kcc-silver hover:text-white" data-testid="asset-drawer-close"><X className="w-5 h-5" /></button>
                        </div>

                        {loading && <div className="mt-8 flex items-center gap-2 kcc-text-silver"><Loader2 className="w-4 h-4 animate-spin" /> Loading passport…</div>}

                        {detail && (
                            <div className="mt-6 space-y-5">
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    {[
                                        ["Health Index", `${detail.asset.health_index} / 100`],
                                        ["Vibration", `${detail.asset.vibration_mms} mm/s`],
                                        ["Temperature", `${detail.asset.temperature_c} °C`],
                                        ["Current", `${detail.asset.current_a} A`],
                                        ["Pressure", `${detail.asset.pressure_bar} bar`],
                                        ["MTBF", `${detail.asset.mtbf_h} h`],
                                        ["MTTR", `${detail.asset.mttr_h} h`],
                                        ["Availability", `${detail.asset.availability_pct}%`],
                                        ["OEE", `${detail.asset.oee_pct}%`],
                                        ["Remaining Life", `${detail.asset.rul_days} d`],
                                        ["Next PM", detail.asset.next_pm.slice(0, 10)],
                                        ["Last PM", detail.asset.last_pm.slice(0, 10)],
                                        ["OEM", detail.asset.manufacturer],
                                        ["YTD Cost", `$${detail.asset.cost_ytd_usd.toLocaleString()}`],
                                    ].map(([k, v]) => (
                                        <div key={k} className="p-2 rounded border border-white/5">
                                            <div className="kcc-label">{k}</div>
                                            <div className="text-white mt-0.5 font-mono-kcc text-[11px]">{v}</div>
                                        </div>
                                    ))}
                                </div>

                                {!!detail.work_orders?.length && (
                                    <div>
                                        <div className="kcc-label flex items-center gap-2 mb-2"><Wrench className="w-3.5 h-3.5 text-kcc-copper-light" /> Recent work orders</div>
                                        <div className="space-y-1.5">
                                            {detail.work_orders.map((w) => (
                                                <div key={w.id} className="p-2.5 rounded border border-white/5 text-xs">
                                                    <div className="flex items-center justify-between">
                                                        <span className="font-mono-kcc text-kcc-copper-light">{w.wo_no}</span>
                                                        <StatusChip status={statusToChip(w.status)}>{w.status}</StatusChip>
                                                    </div>
                                                    <div className="text-white mt-1">{w.description}</div>
                                                    <div className="kcc-text-silver mt-0.5">{w.type} · {w.priority} · ${w.total_cost_usd.toLocaleString()}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {!!detail.predictions?.length && (
                                    <div>
                                        <div className="kcc-label flex items-center gap-2 mb-2"><AlertTriangle className="w-3.5 h-3.5 text-kcc-amber" /> AI predictions</div>
                                        {detail.predictions.map((p) => (
                                            <div key={p.id} className="p-3 rounded border border-white/5 bg-kcc-amber/5 text-xs mb-2">
                                                <div className="text-white">{p.predicted_failure_mode}</div>
                                                <div className="kcc-text-silver mt-1">ETA {p.eta_days}d · {(p.probability * 100).toFixed(0)}% probability · confidence {(p.confidence * 100).toFixed(0)}%</div>
                                                <div className="mt-1 text-white">Recommendation: {p.recommendation}</div>
                                                <div className="mt-1 kcc-text-dim">Requires: {p.requires_approval}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="pt-2 border-t border-white/5">
                                    <div className="kcc-label">Governance</div>
                                    <div className="text-[11px] kcc-text-silver mt-2 leading-relaxed">
                                        Work on this asset requires: valid permit-to-work, LOTO isolation, certified technician, spare-parts availability, and Maintenance Manager approval for P1 events. All actions are audited and signed.
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
